import React, { useCallback, useEffect, useRef, useState } from "react";
import Header from "../../components/header";
import ShoppingItem from "../../components/shopping/item";
import tempImge from "../../images/temp/shopping.png";
import productHooks from "../../hooks/productHooks";
import BannerSlider from "../../components/shopping/bannerSlider";

const Shopping = () => {
  const [productList, setProductList] = useState([]);
  const [bannerList, setBannerList] = useState([]);

  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);

  const [isInitialLoadDone, setIsInitialLoadDone] = useState(false);

  const observerRef = useRef();
  const lastProductRef = useRef();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchBanners = async () => {
      const bannerRes = await productHooks.productBannerList({
        limit: 5,
        location: "product",
      });

      if (!bannerRes.IsError) {
        setBannerList(bannerRes.data.data);
      }
    };
    fetchBanners();
  }, []);

  const loadProducts = useCallback(async (pageNum) => {
    setIsLoading(true);
    setError(null);

    try {
      const productRes = await productHooks.productList({ page: pageNum });

      if (!productRes.IsError) {
        const newProducts = productRes.data.data;

        if (newProducts.length === 0) {
          setHasMore(false);
        } else {
          setProductList((prev) =>
            pageNum === 1 ? newProducts : [...prev, ...newProducts]
          );
        }
      } else {
        setError("상품을 불러오는데 실패했습니다.");
      }
    } catch (err) {
      setError("네트워크 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
      if (pageNum === 1) {
        setIsInitialLoadDone(true);
      }
    }
  }, []);

  useEffect(() => {
    if (!isInitialLoadDone) {
      loadProducts(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const currentLastProductRef = lastProductRef.current;

    const handleObserver = (entries) => {
      const target = entries[0];
      if (target.isIntersecting && hasMore && !isLoading) {
        setPage((prev) => prev + 1);
      }
    };

    const option = {
      root: null,
      rootMargin: "20px",
      threshold: 0.5,
    };

    observerRef.current = new IntersectionObserver(handleObserver, option);

    if (currentLastProductRef) {
      observerRef.current.observe(currentLastProductRef);
    }

    return () => {
      if (observerRef.current && currentLastProductRef) {
        observerRef.current.unobserve(currentLastProductRef);
      }
    };
  }, [isLoading, hasMore, productList.length]);

  useEffect(() => {
    if (page > 1 && !isLoading) {
      loadProducts(page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleRefresh = () => {
    setProductList([]);
    setPage(1);
    setHasMore(true);
    setIsInitialLoadDone(false);
    loadProducts(1);
  };

  return (
    <div className="min-h-[calc(100vh)] flex flex-col pt-[3.125rem] md:pt-[4.375rem] bg-white h-full">
      <Header selectedMenu={2} />
      <div className="pt-[1.875rem]">
        {bannerList.length > 0 && <BannerSlider banners={bannerList} />}

        {error && (
          <div className="text-center py-[1rem] text-red-500">
            <p>{error}</p>
            <button
              onClick={handleRefresh}
              className="mt-[0.5rem] px-[1rem] py-[0.5rem] bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              다시 시도
            </button>
          </div>
        )}

        <div className="text-center w-full">
          {productList.map((product, index) => {
            const isLastProduct = index === productList.length - 1;

            return (
              <div
                key={`${product.id}-${index}`}
                ref={isLastProduct ? lastProductRef : null}
              >
                <ShoppingItem
                  image={product.product_thumbnail || tempImge}
                  title={product.product_title}
                  maker={product.brand_name}
                  price={`${Number(product.product_price).toLocaleString()}원`}
                  link={product.product_link}
                />
              </div>
            );
          })}
        </div>

        {isLoading && (
          <div className="flex justify-center py-[2rem]">
            <div className="animate-spin rounded-full h-[2rem] w-[2rem] border-b-2 border-gray-900"></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Shopping;
